<script setup lang="ts">
import { computed } from "vue";

import Template from "~/components/dumb/Template.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { PRIORITY_COLOR_MAP, UNPRIORITIZED_PRIORITY_LABEL } from "~/constants/priority";
import { PriorityFieldIcon, PriorityIcon, XIcon } from "~/icons";
import { EditorMode, type Priority } from "~/shared/enums";
import type { Property } from "~/shared/types";

const props = defineProps<{
  value: Priority | null;
  showIfEmpty?: boolean;
  showBorder?: boolean;
  showIcon?: boolean;
  property?: Property;
  editorMode: EditorMode;
}>();

const emit = defineEmits<{
  accept: [];
  reject: [];
}>();

const isChipRecommendationMode = computed(() => props.editorMode === EditorMode.CHIP_RECOMMENDATION);
const isBaseChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isChipMode = computed(() => isBaseChipMode.value || isChipRecommendationMode.value);
const isDetailRecommendationMode = computed(() => props.editorMode === EditorMode.DETAIL_RECOMMENDATION);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL || isDetailRecommendationMode.value);
const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isRecommendationMode = computed(() => isChipRecommendationMode.value || isDetailRecommendationMode.value);
const isFormOrAutomationMode = computed(
  () => props.editorMode === EditorMode.FORM || props.editorMode === EditorMode.AUTOMATION
);

const rejectRecommendation = () => {
  emit("reject");
};
</script>

<template>
  <component
    :is="isBoardMode ? Tooltip : Template"
    v-if="value || showIfEmpty"
    :text="isBoardMode ? property?.title : undefined">
    <div
      class="flex items-center justify-center rounded-sm text-md select-none"
      :class="{
        'border border-recommendation-base/50 bg-recommendation-base/30 hover:bg-recommendation-base/40':
          isChipRecommendationMode,
        'hover:bg-opposite/10': isBaseChipMode,
        'border border-oncolor': showBorder && !isChipRecommendationMode,
        'h-[26px] gap-1 py-0.5 text-sm': isChipMode,
        'gap-2': !isChipMode && !isBoardMode,
        'h-5 text-xs': !isChipMode && !isTaskDetailMode && !isFormOrAutomationMode,
        'px-2 text-sm hover:bg-lt': isFormOrAutomationMode || isTaskDetailMode,
        'gap-1 px-1': isBoardMode,
        'px-1': !isFormOrAutomationMode && !isTaskDetailMode && !isBoardMode,
      }">
      <PriorityFieldIcon v-if="isChipMode && !value" class="icon-sm" />
      <PriorityIcon
        v-else-if="value && showIcon"
        :class="isChipMode || isFormOrAutomationMode ? 'icon-sm' : 'icon-xs'"
        :style="{
          color: PRIORITY_COLOR_MAP.get(value),
        }" />
      <span :class="((isTaskDetailMode && !value) || (isFormOrAutomationMode && !value)) && 'text-vlt'">
        {{ value ?? (isChipMode ? "Priority" : isFormOrAutomationMode ? UNPRIORITIZED_PRIORITY_LABEL : "None") }}
      </span>
      <Tooltip v-if="isRecommendationMode" text="Clear AI recommendation">
        <span
          class="rounded-sm hover:bg-recommendation-base/30 dark:hover:bg-recommendation-base/40"
          data-global-popper-ignore
          @click.stop="rejectRecommendation"
          @keydown.enter.stop="rejectRecommendation">
          <XIcon class="icon-xs" />
        </span>
      </Tooltip>
    </div>
  </component>
</template>
